/**
 * Admin access configuration.
 *
 * Source of truth for who is treated as an administrator in the web app.
 *
 * The allowlist is populated from the `ADMIN_EMAIL_ALLOWLIST` environment
 * variable, which is injected at build time by dotenv-webpack (same pattern as
 * `CLIENT_ID` / `AUTHORITY` in `src/authConfig.ts`). The value is a
 * comma-separated list of email addresses, e.g.:
 *
 *   ADMIN_EMAIL_ALLOWLIST=alice@example.com,bob@example.com
 *
 * Secure-by-default: when the variable is unset (or empty), the allowlist is
 * empty and NO user is granted admin access.
 *
 * TODO(ACA-102): This email allowlist is an interim gate. Replace it with the
 * chosen role model (e.g. a role/permission claim on the authenticated user,
 * a subscription-org admin flag, or an Entra group membership) once the
 * user/admin model decision is made. `useIsAdmin()` is the single consumer,
 * so swapping the backing source only touches this file and that hook.
 */

/**
 * Normalize an email for case-insensitive comparison: trim surrounding
 * whitespace and lowercase. Returns an empty string for nullish input.
 */
export const normalizeEmail = (email: string | null | undefined): string =>
    (email ?? '').trim().toLowerCase()

/**
 * Parse a comma-separated list of emails into a normalized, de-duplicated,
 * non-empty array.
 */
export const parseEmailAllowlist = (
    raw: string | null | undefined
): string[] => {
    if (!raw) {
        return []
    }

    const seen = new Set<string>()
    for (const part of raw.split(',')) {
        const normalized = normalizeEmail(part)
        if (normalized) {
            seen.add(normalized)
        }
    }

    return Array.from(seen)
}

/**
 * The configured admin email allowlist (normalized, lowercased).
 *
 * Empty by default — populate via the `ADMIN_EMAIL_ALLOWLIST` env var.
 */
export const adminEmailAllowlist: readonly string[] = parseEmailAllowlist(
    process.env.ADMIN_EMAIL_ALLOWLIST
)

/**
 * Whether a given email is on the admin allowlist. Case-insensitive.
 *
 * Returns `false` for nullish/empty emails and whenever the allowlist is empty,
 * preserving the secure-by-default posture.
 */
export const isAllowlistedAdminEmail = (
    email: string | null | undefined
): boolean => {
    const normalized = normalizeEmail(email)
    if (!normalized) {
        return false
    }

    return adminEmailAllowlist.includes(normalized)
}

/**
 * ACA-183: the address a clinician's "Notify admin" action emails when
 * something (e.g. form generation) fails. Overridable via the
 * `ADMIN_NOTIFY_EMAIL` env var; defaults to the innovation team address already
 * used in the Terms & Conditions / Privacy pages so the button always works
 * even before the env var is configured.
 */
export const adminNotifyEmail: string =
    normalizeEmail(process.env.ADMIN_NOTIFY_EMAIL) ||
    'innovation@tewhatuora.govt.nz'

/**
 * Build a `mailto:` href for a clinician to report a failure to the admin /
 * innovation team, with the subject and body pre-filled. Values are
 * URL-encoded so consult ids and free text are safe in the query string.
 */
export const buildFailureReportMailto = (params: {
    subject: string
    body: string
}): string => {
    const query = new URLSearchParams({
        subject: params.subject,
        body: params.body,
    }).toString()
    return `mailto:${adminNotifyEmail}?${query}`
}
